import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Loader2, UserPlus, CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { authRegister, formatApiError } from "@/lib/api";
import { LEGAL, COPYRIGHT } from "@/lib/legalTexts";
import { LegalDialog } from "@/components/trading/LegalDialog";

export default function Register() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [risk, setRisk] = useState(false);
  const [kvkk, setKvkk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [legalDoc, setLegalDoc] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!terms || !risk || !kvkk) {
      toast.error("Devam etmek için tüm yasal metinleri onaylamalısınız.");
      return;
    }
    if (password.length < 6) { toast.error("Şifre en az 6 karakter olmalıdır."); return; }
    setBusy(true);
    try {
      await authRegister({
        name, email, password,
        accept_terms: terms, accept_risk: risk, accept_kvkk: kvkk,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const ConsentRow = ({ checked, onChange, testid, prefix, docKey }) => (
    <div className="flex items-start gap-3">
      <Checkbox data-testid={testid} checked={checked} onCheckedChange={onChange}
        className="mt-0.5 border-[var(--border-strong)] data-[state=checked]:bg-[var(--pos)] data-[state=checked]:border-[var(--pos)] data-[state=checked]:text-black" />
      <p className="text-xs text-[var(--text-dim)] leading-relaxed">
        {prefix}{" "}
        <button type="button" onClick={() => setLegalDoc(LEGAL[docKey])}
          data-testid={`view-${docKey}`}
          className="text-[var(--accent)] hover:text-[#c4a5ff] underline underline-offset-2">
          {LEGAL[docKey].title}
        </button>
        {" "}metnini okudum ve onaylıyorum.
      </p>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative z-[1]">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="h-11 w-11 rounded-md bg-[var(--pos)] flex items-center justify-center">
            <Bot className="h-6 w-6 text-black" strokeWidth={2.4} />
          </div>
          <div>
            <div className="font-head font-black text-xl tracking-tight">CRYPTOSENSE</div>
            <div className="text-[10px] text-[var(--accent)] tracking-[0.25em]">AI TRADING DESK</div>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-7">
          {done ? (
            <div className="text-center space-y-4 py-4" data-testid="register-success">
              <CheckCircle2 className="h-12 w-12 text-[var(--pos)] mx-auto" />
              <h1 className="font-head font-bold text-xl">Kaydınız Alındı</h1>
              <p className="text-sm text-[var(--text-dim)]">
                Hesabınız yönetici onayından sonra aktifleşecektir. Onaylandığında e-posta ve şifrenizle giriş yapabilirsiniz.
              </p>
              <Button data-testid="go-login" onClick={() => nav("/login")}
                className="w-full h-11 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
                Giriş Ekranına Dön
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h1 className="font-head font-bold text-2xl">Kayıt Ol</h1>
                <p className="text-sm text-[var(--text-dim)] mt-1">Yeni bir hesap oluşturun</p>
              </div>
              <div>
                <Label className="text-xs text-[var(--text-dim)]">Ad Soyad</Label>
                <Input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} required
                  className="mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-sm h-11" />
              </div>
              <div>
                <Label className="text-xs text-[var(--text-dim)]">E-posta</Label>
                <Input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-sm h-11" />
              </div>
              <div>
                <Label className="text-xs text-[var(--text-dim)]">Şifre (en az 6 karakter)</Label>
                <Input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-sm h-11" />
              </div>

              <div className="space-y-3 rounded-lg border border-[var(--border)] bg-[var(--app-bg)] p-4">
                <ConsentRow checked={terms} onChange={setTerms} testid="accept-terms" prefix="" docKey="terms" />
                <ConsentRow checked={risk} onChange={setRisk} testid="accept-risk" prefix="" docKey="risk" />
                <ConsentRow checked={kvkk} onChange={setKvkk} testid="accept-kvkk" prefix="" docKey="kvkk" />
              </div>

              <Button data-testid="register-submit" type="submit" disabled={busy}
                className="w-full h-12 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1.5" /> Kayıt Ol</>}
              </Button>

              <button type="button" onClick={() => nav("/login")}
                className="w-full flex items-center justify-center gap-1 text-xs text-[var(--text-faint)] hover:text-[var(--text-primary)]">
                <ArrowLeft className="h-3 w-3" /> Zaten hesabın var mı? Giriş yap
              </button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-[10px] text-[var(--text-faint)] font-mono">{COPYRIGHT}</p>
      </div>

      <LegalDialog open={!!legalDoc} onOpenChange={(o) => !o && setLegalDoc(null)} doc={legalDoc} />
    </div>
  );
}
